import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps } from '@@partner:types:app'
import type { BusinessProfileFormState } from './BusinessProfileFormState'

import React, { useCallback, useEffect } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'

import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../helpers/notification'
import {
  makeBusinessProfileFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './BusinessProfileFormState'

import { Button, CheckPicker, Col, Form, Grid, Panel, Row, SelectPicker, Tag, Toggle } from 'rsuite'
import { SaveButton } from '@components/common/form/Buttons'
import CustomerPhone from '../order/form/CustomerPhone'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import { useFetchSingleBusinessProfile } from '@@partner:graphql/query/businessProfiles/singleBusinessProfile.gql'
import { useMutateSaveBusinessProfile } from '@@partner:graphql/mutation/businessProfile/saveBusinessProfile.gql'
import { getSearchableBusinessProfilesStatus } from '@@partner:helpers/businessProfiles'
import { formatDate, formatPhone, normalizePhoneNumber } from '@@partner:helpers/util'
import { DownloadButton } from '@components/common/DownloadButton'
import AppLoader from '@components/common/AppLoader'
import FormCheckbox from '@components/common/form/FormCheckbox'
import { BUSINESS_PROFILE } from '@hive/status'

export interface CustomerFormProps extends CrudFormProps {
  profileId: GQL_ID
}

const AVAILABLE_CUSTOMER_STATUSES = getSearchableBusinessProfilesStatus()


export default function BusinessProfileForm ({
  profileId,
  onSaved
}: CustomerFormProps) {

  const { formState, setFormState, setFormRef } =
    useValidatableForm<BusinessProfileFormState>(makeBusinessProfileFormState)

  const {
    data,
    loading: loadingBusinessProfile,
    error: errorLoadingBusinessProfile
  } = useFetchSingleBusinessProfile({
    id: profileId
  })

  const [ saveBusinessProfile, { loading: loadingSaveProfile, error: errorSavingProfile} ] =
    useMutateSaveBusinessProfile()

  useEffect(() => {
    if (!loadingBusinessProfile && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.singleBusinessProfile))
      )
    }
  }, [ data, loadingBusinessProfile, setFormState ])

  const businessProfile = data?.singleBusinessProfile

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )
  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveBusinessProfile({
          variables: {
            businessProfileInfo: mapStateToApiVariable(formState),
            id: profileId,
            activate: businessProfile?.status === BUSINESS_PROFILE.PENDING && formState.status === BUSINESS_PROFILE.ACTIVE ? true : false
          }
        })
          .then(({ data }) => {
            notifyOk('Компания сохранена')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.businessProfile)
            )

            onSaved?.(data?.businessProfile.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ saveBusinessProfile, formState, setFormState, onSaved, profileId ]
  )

  if (errorLoadingBusinessProfile) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить компанию #${profileId} для редактирования`}
        error={errorLoadingBusinessProfile}
      />
    )
  }

  if (loadingBusinessProfile) {
    return <AppLoader content={'Загрузка бизнес-профиля' } />
  }

  if (errorLoadingBusinessProfile || !businessProfile || !formState.id) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить данные бизнес-профиля #${profileId} для редактирования`}
        error={errorLoadingBusinessProfile}
      />
    )
  }

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      ref={setFormRef}
      layout='vertical'
      fluid
    >
      
      <Grid fluid>
        <Col xs={24} md={16}>
          <Panel 
            shaded
            header={`${businessProfile.name}`}
            className='my-5'
          >
            <div style={{ 
              fontSize: 16, 
              fontWeight: '700',
              marginBottom: 20
            }}>БИН/ИИН { businessProfile.tin }</div>
            <Form.Group>
              <Form.ControlLabel>Название</Form.ControlLabel>
              <Form.Control name="name" placeholder="Название" />
            </Form.Group>
      
            <Form.Group>
              <Form.ControlLabel>Адрес</Form.ControlLabel>
              <Form.Control name="address" placeholder="Адрес" />
            </Form.Group>
      
            <Form.Group>
              <CustomerPhone name="phone_number" label="Телефон компании" />
            </Form.Group>
      
            <Form.Group>
              <Form.ControlLabel>Статус</Form.ControlLabel>
              <Form.Control 
                name='status' 
                value={formState.status}
                accepter={SelectPicker} 
                data={AVAILABLE_CUSTOMER_STATUSES} 
              />
            </Form.Group>


          </Panel>
          <Panel 
            shaded
            header='Дополнительная информация'
            className='my-5'
          >
            <Form.Group>
              <Form.ControlLabel>Директор</Form.ControlLabel>
              <Form.Control name="director" placeholder="Директор" />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Требуется счёт-фактура</Form.ControlLabel>
              <FormCheckbox.Control name='require_invoice' placeholder='Треубется счет-фактура' />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Плательщик НДС</Form.ControlLabel>
              <FormCheckbox.Control name='vat_enabled' placeholder='Плательщик НДС' />
            </Form.Group>
          </Panel>
          <Panel
            shaded
            header='Банковские реквизиты'
          >
            <Form.Group>
              <Form.ControlLabel>Банк</Form.ControlLabel>
              <Form.Control name="bankName" placeholder="Банк" />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Расчётный счёт</Form.ControlLabel>
              <Form.Control name="accountNumber" placeholder="Расчётный счёт" />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>БИК</Form.ControlLabel>
              <Form.Control name="bik" placeholder="БИК" />
            </Form.Group>
          </Panel>
        </Col>
        <Col xs={24} md={8}>
          <Panel
            shaded
            className='my-5'
          >
            { businessProfile.xid && (
              <Col xs={businessProfile.type ? 12 : 24}>
                <h3 style={{ fontSize: 16, fontWeight: '700' }}>ID в учетной системе</h3>
                <div>{ businessProfile.xid }</div>
              </Col>
            )}
            { businessProfile.type && (
              <Col xs={businessProfile.xid ? 12 : 24}>
                <h3 style={{ fontSize: 16, fontWeight: '700' }}>Тип</h3>
                <div>{ businessProfile.type }</div>
              </Col>
            )}
            <Col xs={12}>
              { businessProfile.created_at && (
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: '700' }}>Создана</h3>
                  <div>{ formatDate(new Date(businessProfile.created_at)) }</div>
                </div>
              )}
            </Col>
            <Col xs={12}>
              { businessProfile.updated_at && (
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: '700' }}>Обновлена</h3>
                  <div>{ formatDate(new Date(businessProfile.updated_at)) }</div>
                </div>
              )}
            </Col>
            
            <Col className='my-5' xs={24}>
              <h3 style={{ fontSize: 16, fontWeight: '700' }}>Документы</h3>
              <Row className='my-3'>
                <h3 style={{ fontSize: 16, fontWeight: '700' }}>Скан регистрации компании</h3>
                { businessProfile.registrationScan?.url && (
                  <DownloadButton 
                    fileName={`${businessProfile.tin}:registration`}
                    url={businessProfile.registrationScan?.url}
                  />
                )}
              </Row>
              <Row className='my-3'>
                <h3 style={{ fontSize: 16, fontWeight: '700' }}>Скан директора компании</h3>
                { businessProfile.directorConfirmScan?.url && (
                  <DownloadButton 
                    fileName={`${businessProfile.tin}:director`}
                    url={businessProfile.directorConfirmScan.url}
                  />
                )}
              </Row>
              <Row className='my-3'>
                <h3 style={{ fontSize: 16, fontWeight: '700' }}>Скан директора компании</h3>
                { businessProfile.attorneyScan?.url && (
                  <DownloadButton 
                    fileName={`${businessProfile.tin}:attorney`}
                    url={businessProfile.attorneyScan.url}
                  />
                )}
              </Row>
            </Col>
          </Panel>
          { businessProfile.owner && (
            <Panel
              shaded
              className='my-5'
              header='Владелец'
            >
              <div>{ businessProfile?.owner?.first_name } { businessProfile?.owner?.last_name }</div>
              <a href={`tel:${normalizePhoneNumber(businessProfile.owner.phone)}`}>{ formatPhone(businessProfile.owner.phone) }</a>
              <div className='my-5'>
                <Button appearance='ghost'>
                  Изменить
                </Button>
              </div>
            </Panel>
          )}
        </Col>
      </Grid>

      <SaveButton
        loading={loadingBusinessProfile || loadingSaveProfile}
        disabled={loadingBusinessProfile || loadingSaveProfile}
      >
        Сохранить
      </SaveButton>
    </Form>
  )
}
